// src/pages/dashboard/DashboardPage.tsx — "Record Book" identity rebuild
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PlusIcon, ArrowRightIcon, UsersIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import apiService from '../../services/api';
import Goalpost from '../../components/common/Goalpost';
import { useSeasonNow, datelineFor } from '../../hooks/useSeasonNow';
import { byRelevance, isInSeason, standingSummary } from '../../utils/leagueStanding';
import StatusChip from '../../components/league/StatusChip';

export default function DashboardPage() {
  const { user } = useAuth();

  const { data: recentLeagues, loading: leaguesLoading, error: leaguesError, refetch: refetchLeagues } = useApi(
    () => apiService.getLeagues()
  );

  const season = useSeasonNow();

  const firstName =
    (user?.display_name || user?.displayName || user?.username || 'Manager').split(' ')[0];

  const leaguesCount = recentLeagues?.length || 0;
  const activeCount = recentLeagues?.filter(isInSeason).length || 0;
  const hasLeagues = leaguesCount > 0;

  // The ledger reports the manager, not the league. "NFL Kickers 32 / NFL
  // Punters 32" was the same pair of constants for every user forever — two of
  // four figures that could never move. These do.
  const myTeams = (recentLeagues ?? []).map(l => l.my_team).filter(Boolean);
  const wins = myTeams.reduce((n, t) => n + (t!.wins || 0), 0);
  const losses = myTeams.reduce((n, t) => n + (t!.losses || 0), 0);
  const pointsFor = myTeams.reduce((n, t) => n + (t!.points_for || 0), 0);

  const ledger: { n: React.ReactNode; label: string }[] = [
    { n: leaguesCount, label: 'Leagues' },
    { n: activeCount, label: 'In Season' },
    ...(myTeams.length
      ? [
          { n: `${wins}–${losses}`, label: 'Record' },
          { n: pointsFor.toFixed(1), label: 'Points For' },
        ]
      : []),
  ];

  return (
    <div className="space-y-10">
      {/* ── Masthead ─────────────────────────────────────────────── */}
      <header className="border-b-2 border-ink-900 pb-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            {/* Dateline. A record book is a periodical; ink, not gold — gold
                is for the one CTA and genuinely live states. */}
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary-500">
              {datelineFor(season)}
            </p>
            <h1 className="mt-2 font-display uppercase leading-none text-ink-900 text-4xl sm:text-5xl">
              Welcome back, {firstName}
            </h1>
            <p className="mt-2 text-secondary-600">
              Your leagues, drafts, and standings — filed and current.
            </p>
          </div>

          {/* Secondary index — replaces the old quick-action card grid */}
          <nav className="flex items-center gap-5 text-xs font-semibold uppercase tracking-wider">
            <Link
              to="/app/players"
              className="inline-flex items-center gap-1.5 text-secondary-600 hover:text-ink-900 transition-colors"
            >
              <UsersIcon className="w-4 h-4" />
              Browse Players
            </Link>
            <Link
              to="/app/scoring"
              className="inline-flex items-center gap-1.5 text-secondary-600 hover:text-ink-900 transition-colors"
            >
              <ChartBarIcon className="w-4 h-4" />
              Scoring Rules
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Your Leagues (the hero) ──────────────────────────────── */}
      <section>
        {/* The masthead and its actions share a line once there is room for
            both. At phone width they competed and "Your Leagues" broke across
            two lines, which a section masthead should never do — so the rule
            gets its own row and the actions sit under it. */}
        <div className="flex flex-col gap-2 border-b border-secondary-300 pb-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <h2 className="font-display uppercase text-xl tracking-wide text-ink-900 whitespace-nowrap">
            Your Leagues
          </h2>
          <div className="flex items-center gap-4">
            {hasLeagues && (
              <Link
                to="/app/leagues"
                className="text-xs font-semibold uppercase tracking-wider text-secondary-600 hover:text-ink-900 transition-colors"
              >
                View all
              </Link>
            )}
            <Link
              to="/app/leagues/browse"
              className="text-xs font-semibold uppercase tracking-wider text-secondary-600 hover:text-ink-900 transition-colors"
            >
              Browse open
            </Link>
            <Link
              to="/app/leagues/create"
              className="inline-flex items-center gap-1.5 bg-gold px-3.5 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-gold-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Create League
            </Link>
          </div>
        </div>

        {leaguesLoading ? (
          <div className="divide-y divide-secondary-200">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex items-center justify-between py-4 animate-pulse">
                <div className="space-y-2">
                  <div className="h-4 w-40 bg-secondary-200" />
                  <div className="h-3 w-56 bg-secondary-100" />
                </div>
                <div className="h-4 w-4 bg-secondary-200" />
              </div>
            ))}
          </div>
        ) : leaguesError ? (
          <div className="py-12 text-center">
            <p className="font-display uppercase text-lg text-ink-900">Couldn’t load your leagues</p>
            <p className="mt-1 text-sm text-secondary-600">
              Something interrupted the connection. Give it another try.
            </p>
            <button
              onClick={() => refetchLeagues().catch(() => {})}
              className="mt-4 inline-flex items-center gap-1.5 border-2 border-ink-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
            >
              Retry
            </button>
          </div>
        ) : !hasLeagues ? (
          <div className="py-12 text-center">
            <Goalpost className="w-14 h-12 mx-auto mb-4 text-gold" />
            <p className="font-display uppercase text-lg text-ink-900">No leagues on the books yet</p>
            <p className="mt-1 mb-5 text-sm text-secondary-600">
              Start one of your own, or join a league that is already taking members.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/app/leagues/create"
                className="inline-flex items-center gap-1.5 bg-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-gold-600 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Create Your First League
              </Link>
              <Link
                to="/app/leagues/browse"
                className="inline-flex items-center gap-1.5 border border-ink-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
              >
                Browse Open Leagues
              </Link>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-secondary-200">
            {[...recentLeagues!].sort(byRelevance).slice(0, 3).map(league => {
              const status = league.league_status || league.leagueStatus || 'setup';
              const { segments, isLive } = standingSummary(league);

              return (
                <li key={league.id}>
                  <Link
                    to={`/app/leagues/${league.id}`}
                    className="group flex items-center justify-between gap-4 py-4 -mx-2 px-2 hover:bg-secondary-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <h3 className="font-semibold text-ink-900">{league.name}</h3>
                        <StatusChip status={status} />
                        {isLive && (
                          <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-wider text-gold-700">
                            <span className="w-1.5 h-1.5 bg-gold" aria-hidden="true" />
                            Live
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-secondary-600">
                        {segments.join(' · ')}
                      </p>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 shrink-0 text-secondary-400 group-hover:text-ink-900 transition-colors" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {/* ── Ledger stat line ───────────────────────────────────── */}
        {/* Source order is dt-then-dd, as a definition list requires;
            flex-row-reverse puts the figure first the way a ledger reads. */}
        <dl className="mt-6 flex flex-wrap items-baseline gap-x-8 gap-y-3 border-t-2 border-ink-900 pt-4">
          {ledger.map(stat => (
            <div key={stat.label} className="flex flex-row-reverse items-baseline gap-2">
              <dt className="text-xs font-semibold uppercase tracking-wider text-secondary-500">
                {stat.label}
              </dt>
              <dd className="font-display text-3xl leading-none text-ink-900 tabular-nums">
                {stat.n}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ── How It Works — first-run only ────────────────────────── */}
      {!leaguesLoading && !leaguesError && !hasLeagues && (
        <section>
          <h2 className="font-display uppercase text-xl tracking-wide text-ink-900 border-b border-secondary-300 pb-2">
            How It Works
          </h2>
          <ol className="mt-4 space-y-5">
            {[
              {
                t: 'Create or Join a League',
                d: 'Start your own league and invite friends with a join link, or use an invite code a commissioner sent you.',
              },
              {
                t: 'Draft Your Specialists',
                d: 'Snake-draft a 6-spot roster: 3 kickers and 3 punters. Yes, the kicker goes first here.',
              },
              {
                t: 'Set Your Lineup Every Week',
                d: 'Start 2 kickers and 2 punters in head-to-head matchups. Field goals, PATs, distance bonuses, and punts inside the 20 all score.',
              },
            ].map((step, i) => (
              <li key={step.t} className="flex items-baseline gap-4">
                <span className="font-display text-2xl leading-none text-secondary-400 tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h4 className="font-semibold text-ink-900">{step.t}</h4>
                  <p className="mt-0.5 text-sm text-secondary-600">{step.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
